import { ReactTable, type RowData } from '@/lib/tanstack-table';
import { useSelector } from '@tanstack/react-store';
import { GearSixIcon } from '@phosphor-icons/react';
import { T } from 'gt-react';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

export function DataTableViewOptions<TData extends RowData>({
  table,
  columnLabels,
  onResetLayout,
}: {
  table: ReactTable<TData>;
  /**
   * Display names keyed by column id. Falls back to the raw id (capitalized)
   * for columns not in the map.
   */
  columnLabels?: Record<string, string>;
  /** When provided, adds a "Reset layout" action that calls it. */
  onResetLayout?: () => void;
}) {
  const columnVisibility = useSelector(table.atoms.columnVisibility);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="ml-auto hidden h-8 lg:flex"
        >
          <GearSixIcon />
          <T context="button label to open column visibility options">View</T>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[200px]">
        <DropdownMenuLabel>
          <T context="dropdown section heading for showing/hiding table columns">
            Toggle columns
          </T>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {table
          .getAllColumns()
          .filter(
            (column) =>
              typeof column.accessorFn !== 'undefined' && column.getCanHide(),
          )
          .map((column) => {
            const label = columnLabels?.[column.id];
            return (
              <DropdownMenuCheckboxItem
                key={column.id}
                className={label ? undefined : 'capitalize'}
                checked={columnVisibility[column.id] ?? true}
                onCheckedChange={(value) => column.toggleVisibility(!!value)}
                // The menu closes on select by default, which makes toggling
                // several columns tedious — keep it open until the user
                // dismisses it (click-away / Esc). Under Radix this was an
                // onSelect handler calling preventDefault; Base UI decides
                // before the handler runs, so it is a prop now.
                closeOnClick={false}
              >
                {label ?? column.id}
              </DropdownMenuCheckboxItem>
            );
          })}
        {onResetLayout && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={onResetLayout}>
              <T context="menu action, restore the table's default column layout">
                Reset layout
              </T>
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
